import axios from 'axios'
import { ENDPOINTS } from 'const/endpoints'
import { errorNotify } from 'utils/toster'
import { CommonMessages } from 'utils/messages'
import { ReactComponent as ErrorFound } from 'assets/images/PageNotWorking.svg'
import Button from 'components/Button'
import {
	dispatchWindowEvent,
	getLocalStorageDetails,
	onParseJSON,
	removeLocalStorageDetails,
	setLocalStorageDetails,
} from 'utils/common'
import { EVENT_NAME, LOCAL_STORAGE } from 'const/common'
import { socket } from 'components/SocketConectivity/socketConnection'
import { UIToolkit } from '@zoom/videosdk-ui-toolkit'
import { APP_ROUTES } from 'const/router'

export function createActivityLog(error: any) {
	const domainName = window.location.hostname
	const screen = window.location.pathname.slice(1)
	const serializedError = JSON.stringify(error?.stack as Error)

	if (serializedError) {
		const requestData = {
			source: 'web/frontend',
			action: error?.name,
			screen,
			error: serializedError ?? '',
			isEmail: true,
			domainName,
		}
		const token = localStorage.getItem('token')
		const config = {
			url: `${process.env.REACT_APP_API_URL}${ENDPOINTS.ACTIVITY_LOG}`,
			headers: {
				Authorization: `Bearer ${token}`,
			},
			method: 'post',
			data: requestData,
		}
		axios(config)
			.then(({ data }) => {
				console.log(data)
			})
			.catch((err: string) => {
				errorNotify(err)
			})
	}
}

export function ErrorFallback({
	error,
	resetErrorBoundary,
}: {
	error: Error
	resetErrorBoundary: () => void
}) {
	createActivityLog(error as Error)
	return (
		<div role="alert" className="flex flex-col items-center justify-center h-full flex-1 gap-4">
			<p>{`${CommonMessages?.ERROR_HANDLING_MESSAGE}:`}</p>
			<pre>
				{' '}
				<ErrorFound />{' '}
			</pre>
			<Button onClick={resetErrorBoundary}>Try again</Button>
		</div>
	)
}
/** Func handleStorageChange is used to get local storage and process permission
 * data, Iterate through each service object in permission data and process it
 *  invoke sevice object and return route details
 *
 * @returns Return the array containing processed route details
 */
export const handleStorageChange = () => {
	const permission: any = getLocalStorageDetails(LOCAL_STORAGE?.PERMISSION_DETAIL)
	const routeDetails: any = []
	if (permission) {
		const permissionData = onParseJSON(permission)
		if (permissionData) {
			Array.isArray(permissionData) &&
				permissionData?.forEach((serviceObj: any) => {
					processServiceObject(serviceObj, routeDetails)
				})
		}
	}
	return routeDetails
}

/**
 * Handle beforeunload event to disconnect the socket
 * Notifies the user to check their socket connection.
 */
export const isConferenceGoing = () => {
	const conferenceDetail = getLocalStorageDetails(LOCAL_STORAGE.CONFERENCE_DETAILS)
	const conferenceData = onParseJSON(conferenceDetail)
	if (conferenceDetail && conferenceData) {
		return true
	}
	return false
}

export const createHandleUnload = (uitoolkit: UIToolkit) => {
	return () => {
		const pathName = window.location.pathname
		const isConferenceRoute =
			pathName?.includes(APP_ROUTES.CONFERENCE_SCHEDULE) ||
			pathName?.includes(APP_ROUTES.GLOBAL_CONFERENCE_SCHEDULE) ||
			pathName?.includes(APP_ROUTES.JOIN_CONFERENCE_MEETING)
		const isConferenceOnGoing = isConferenceGoing()
		if (uitoolkit && isConferenceOnGoing && !isConferenceRoute) {
			uitoolkit.leaveSession()
			removeLocalStorageDetails(LOCAL_STORAGE.CONFERENCE_DETAILS)
			dispatchWindowEvent(EVENT_NAME.STORAGE)
		}
	}
}

export const onBeforeUnload = (e: any) => {
	const pathName = window.location.pathname
	const isConferenceRoute =
		pathName?.includes(APP_ROUTES.CONFERENCE_SCHEDULE) ||
		pathName?.includes(APP_ROUTES.GLOBAL_CONFERENCE_SCHEDULE) ||
		pathName?.includes(APP_ROUTES.JOIN_CONFERENCE_MEETING)

	const isConferenceOnGoing = isConferenceGoing()
	if (isConferenceOnGoing && !isConferenceRoute) {
		e.preventDefault()
		e.returnValue = '' // Only triggers dialog if needed
	}
	if (socket?.connected) {
		socket?.disconnect()
	}
}
/** Func processModuleObject is used for Processes a module object  by iterating
 *  through its provider modules and pushing provider screens to full details
 */

const processModuleObject = (moduleObj: any, fullDetails: any[]): void => {
	// Destructure providerModule and its providerScreens
	const { providerModule } = moduleObj
	const { providerScreens = [], children = [] } = providerModule || {}

	// Push providerScreens to fullDetails
	fullDetails?.push(...providerScreens)

	// Process childScreens and push to fullDetails
	children?.forEach((child: any) => {
		const childScreens = child?.providerScreens || []
		fullDetails?.push(...childScreens)
	})
}

// ===================old function will remove later ==============
// ==============================
// const processModuleObject = (moduleObj: any, fullDetails: any[]) => {
// 	const {
// 		providerModule: { providerScreens },
// 	} = moduleObj
// 	fullDetails.push(...(providerScreens || []))
// }

/** Func processServiceObject is used for Processes a service object  by iterating
 *  through its provider service modules and invoking the 'processModuleObject'
 *  function for each module.
 */
export const processServiceObject = (serviceObj: any, fullDetails: any[]) => {
	const { providerServiceModules } = serviceObj
	providerServiceModules?.forEach((moduleObj: any) => {
		processModuleObject(moduleObj, fullDetails)
	})
}
/**
 * Adds an event listener to the window object with the specified event name, callback function,
 * and an optional AbortController signal for controlling the event listener's lifecycle.
 *
 * @param addEventName - The name of the event to listen for (e.g., "click", "resize").
 * @param addFunction - The callback function to execute when the event is triggered.
 * @param ac - An optional AbortController or its signal to manage the event listener's lifecycle.
 *             Pass `ac.signal` to allow the event listener to be removed when the AbortController is aborted.
 */
export const windowAddEventListener = (
	addEventName: string,
	addFunction: EventListenerOrEventListenerObject,
	ac?: AbortController,
) => {
	window.addEventListener(addEventName, addFunction, ac)
	// 	window.addEventListener(addEventName, addFunction, ac?.signal ? { signal: ac.signal } : undefined)
}
/**
 * Removes an event listener from the global window object.
 *
 * @param {string} removeEventName - The name of the event for which the listener was added.
 * @param {Function} removeFunction - The function to be removed as the event listener.
 */
export const windowRemoveEventListener = (removeEventName: string, removeFunction: any) => {
	window.removeEventListener(removeEventName, removeFunction)
}
